import React from 'react'

import { useFormContext } from 'react-hook-form'
import type { Props as ModalProps } from 'react-modal'

import { useMeasure } from '@hooks'

import { Button, Typography } from '@components/ui'

import { CloseButtonMessage } from '@messages'
import { DialogResponseActions, DialogResponseWrapper } from './styles'

interface DialogResponseProps {
  aria: Required<ModalProps>['aria']
  children: NonNullable<React.ReactNode>
  error: string | null
  onClose: VoidFunction
  onErrorText: NonNullable<React.ReactNode>
  onSubmitSuccessText: NonNullable<React.ReactNode>
}

/**
 * DialogResponse is a component that is used to display a response to a form submission or an error.
 *
 * @param {DialogResponseProps} {
 *   aria, - aria attributes for the dialog
 *   children, - the form
 *   error, - the error message
 *   onClose, - the function to call when the dialog is closed
 *   onErrorText, - the text to display when there is an error
 *   onSubmitSuccessText, - the text to display when the form is submitted successfully
 * }
 */
export function DialogResponse({
  aria,
  children,
  error,
  onClose,
  onErrorText,
  onSubmitSuccessText,
}: DialogResponseProps) {
  // store and control width and height separately as height values can change more often than width
  const {
    formState: { isSubmitSuccessful, isSubmitted },
  } = useFormContext()
  const [savedFormWidth, setSavedFormWidth] = React.useState(0)
  const [savedFormHeight, setSavedHeight] = React.useState(0)

  const [formMeasureRef, { width: formWidth, height: formHeight }] = useMeasure()

  // update savedWidth when width is above 0
  React.useEffect(() => {
    if (formWidth > 0) {
      setSavedFormWidth(formWidth)
    }
  }, [formWidth])

  // update savedHeight when height is above 0
  React.useEffect(() => {
    if (formHeight > 0) {
      setSavedHeight(formHeight)
    }
  }, [formHeight])

  const success = isSubmitSuccessful && isSubmitted

  if (error || success) {
    return (
      <DialogResponseWrapper
        id={aria.describedby}
        // update width and height like this to avoid infinite amount of calculated classes
        style={{
          '--width-dialog-response': savedFormWidth > 0 ? `${savedFormWidth}px` : 'auto',
          '--min-height-dialog-response': `${savedFormHeight}px`,
        }}
      >
        {error && (
          <>
            <Typography size="400-sm" color="gray-50" align="center">
              {onErrorText}
            </Typography>
            <Typography size="400-sm" color="error" align="center">
              {error}
            </Typography>
          </>
        )}
        {success && (
          <Typography size="400-sm" color="gray-50" align="center">
            {onSubmitSuccessText}
          </Typography>
        )}
        <DialogResponseActions>
          <Button onClick={onClose} variant="primaryContained" type="button" data-testid="dialog-response-close-btn">
            {CloseButtonMessage}
          </Button>
        </DialogResponseActions>
      </DialogResponseWrapper>
    )
  }

  return <div ref={formMeasureRef}>{children}</div>
}
